import { useState, useEffect, useRef } from 'react';

export type ScrollDirection = 'up' | 'down' | 'idle';

interface Options {
  threshold?: number;
  topOffset?: number;
}

/**
 * Tracks the page scroll direction. Useful for auto-hiding sticky chrome
 * (e.g. the top navigation) when the user is scrolling down to consume
 * content, and revealing it again on scroll-up.
 *
 * - Returns `'idle'` while at or near the top of the page (above `topOffset`).
 * - Ignores micro scrolls smaller than `threshold` to avoid jitter.
 */
export const useScrollDirection = (
  { threshold = 8, topOffset = 64 }: Options = {},
): ScrollDirection => {
  const [direction, setDirection] = useState<ScrollDirection>('idle');
  const lastYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastYRef.current;

        if (currentY <= topOffset) {
          setDirection(prev => (prev === 'idle' ? prev : 'idle'));
          lastYRef.current = currentY;
        } else if (Math.abs(delta) >= threshold) {
          const next: ScrollDirection = delta > 0 ? 'down' : 'up';
          setDirection(prev => (prev === next ? prev : next));
          lastYRef.current = currentY;
        }

        tickingRef.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold, topOffset]);

  return direction;
};
